import { Link } from "@tanstack/react-router";
import type { Timestamp } from "firebase/firestore";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";

import { EmptyState, LoadingRows } from "components/states";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { FeedbackFormView } from "./FeedbackFormView";
import { formatFormTime } from "./format";
import { windowState } from "./logic";
import type { Answers, FeedbackForm, Submission } from "./schemas";
import {
  submitFeedback,
  useFeedbackForm,
  useOwnSubmission,
  type DocState,
} from "./use-feedback";

/**
 * A REQUESTED submission older than this is being retried by the backend
 * sweep. Matches STALE_AFTER_SECONDS in feedback_submission.py.
 */
const STALE_REQUEST_MS = 2 * 60 * 1000;

/** The backend's NOT_RECEIVED, set once the sweep gives up on a submission. */
export const NOT_RECEIVED =
  "We didn't receive your answers. Please send them again.";

/** Re-render every half minute so an opening or closing time takes effect. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function FeedbackFormPage({
  eventId,
  slug,
  formId,
  uid,
  timeZone,
}: {
  eventId: string;
  slug: string;
  formId: string;
  uid: string;
  timeZone?: string;
}) {
  const form = useFeedbackForm(eventId, formId);
  const submission = useOwnSubmission(eventId, formId, uid);

  return (
    <div className="mx-auto min-w-0 max-w-2xl space-y-4">
      <Button asChild variant="ghost" className="h-11 px-2">
        <Link to="/e/$slug" params={{ slug }} data-testid="feedback-back">
          <ArrowLeft className="h-4 w-4" />
          Back to the event
        </Link>
      </Button>
      <FormBody
        form={form}
        submission={submission}
        eventId={eventId}
        formId={formId}
        uid={uid}
        timeZone={timeZone}
      />
    </div>
  );
}

function FormBody({
  form,
  submission,
  eventId,
  formId,
  uid,
  timeZone,
}: {
  form: DocState<FeedbackForm>;
  submission: DocState<Submission>;
  eventId: string;
  formId: string;
  uid: string;
  timeZone?: string;
}) {
  const now = useNow();
  const [sending, setSending] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  if (form.status === "loading" || submission.status === "loading") {
    return <LoadingRows rows={4} />;
  }

  // A member may read open forms only, so a closed one answers with a
  // permission error rather than an empty document. Both mean the same here.
  if (
    form.status === "not-found" ||
    (form.status === "error" && form.code === "permission-denied")
  ) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="This form is not available"
        testId="feedback-unavailable"
      >
        It may have closed, or the link may be wrong.
      </EmptyState>
    );
  }
  if (form.status === "error") {
    return (
      <EmptyState icon={AlertCircle} title="Something went wrong">
        {form.error}
      </EmptyState>
    );
  }

  const current = form.data;
  const own = submission.status === "success" ? submission.data : null;
  const state = windowState(current, now);

  if (state !== "accepting") {
    return (
      <EmptyState
        icon={ClipboardList}
        title={
          state === "not-yet-open"
            ? "This form is not open yet"
            : "This form is closed"
        }
        testId="feedback-not-accepting"
      >
        {state === "not-yet-open" && current.opensAt
          ? `It opens ${formatFormTime(current.opensAt, timeZone)}.`
          : own?.status === "SUCCEEDED" || own?.status === "QUEUED"
            ? "Thank you, your answers were recorded."
            : "It no longer takes answers."}
      </EmptyState>
    );
  }

  if (
    own?.anonymous &&
    (own.status === "SUCCEEDED" || own.status === "QUEUED")
  ) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="Thank you for your feedback"
        testId="feedback-done"
      >
        Your anonymous answers were received. They are added to the results
        together with other answers within a few minutes, and can't be changed
        or linked to you.
      </EmptyState>
    );
  }

  // Never re-enabled while REQUESTED: past two minutes the backend sweep is
  // retrying it, and it ends SUCCEEDED or FAILED with a message either way.
  const submittedAt = own?.submittedAt as Timestamp | null | undefined;
  const requested = own?.status === "REQUESTED";
  const stale =
    requested &&
    submittedAt != null &&
    now.getTime() - submittedAt.toMillis() > STALE_REQUEST_MS;
  const pending = requested && !stale;

  const submit = (answers: Answers) => {
    setSending(true);
    setWriteError(null);
    submitFeedback({
      eventId,
      formId,
      uid,
      answers,
      anonymous: current.anonymous,
    })
      .catch((error: unknown) => {
        const denied =
          typeof error === "object" &&
          error !== null &&
          (error as { code?: unknown }).code === "permission-denied";
        setWriteError(
          denied
            ? "Your answers could not be sent. The form may have closed or changed; reload the page and try again."
            : "Your answers could not be sent. Check your connection and try again.",
        );
      })
      .finally(() => setSending(false));
  };

  return (
    <Card className="border-lit-card-edge shadow-lit-card">
      <CardContent className="space-y-4 pt-6">
        {pending && (
          <p
            role="status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="feedback-pending"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending your answers…
          </p>
        )}
        {stale && (
          <p
            role="status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="feedback-still-sending"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Still sending… we'll keep trying.
          </p>
        )}
        {own?.status === "SUCCEEDED" && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-lg border border-status-success bg-status-success/15 px-3 py-2 text-sm text-foreground"
            data-testid="feedback-saved"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            Thank you, your answers were saved. You can change them until the
            form closes.
          </p>
        )}
        {own?.status === "FAILED" && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-status-error bg-status-error/15 px-3 py-2 text-sm text-foreground wrap-anywhere"
            data-testid="feedback-failed"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span className="min-w-0">
              {own.error === NOT_RECEIVED
                ? "We didn't receive this, please send again."
                : `${own.error ?? "Your answers could not be recorded."} Please check the form and send it again.`}
            </span>
          </p>
        )}
        {writeError && (
          <p
            role="alert"
            className="rounded-lg border border-status-error bg-status-error/15 px-3 py-2 text-sm text-foreground"
            data-testid="feedback-write-error"
          >
            {writeError}
          </p>
        )}
        <FeedbackFormView
          form={current}
          initialAnswers={own?.answers}
          submitting={sending || requested}
          submitLabel={
            own?.status === "SUCCEEDED" ? "Update feedback" : "Submit feedback"
          }
          onSubmit={submit}
        />
      </CardContent>
    </Card>
  );
}
